import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiUsers, FiMoreVertical, FiEdit2, FiTrash2, FiCheck, FiAlertCircle, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import * as groupsApi from '../../api/groups';
import { useAuth } from '../../context/AuthContext';

type Group = groupsApi.Group;

const ALL_PERMISSIONS = [
  { key: 'read', label: 'Lecture', desc: 'Voir les ressources' },
  { key: 'write', label: 'Écriture', desc: 'Créer et modifier' },
  { key: 'delete', label: 'Suppression', desc: 'Supprimer des ressources' },
  { key: 'deploy', label: 'Déploiement', desc: 'Lancer des exécutions' },
  { key: 'manage_projects', label: 'Gérer projets', desc: 'Créer et archiver des projets' },
  { key: 'manage_users', label: 'Gérer utilisateurs', desc: 'Inviter et gérer les membres' },
  { key: 'admin', label: 'Administration', desc: 'Acces complet' },
];

const PERM_COLOR: Record<string, string> = {
  read: 'bg-sky-100 text-sky-700',
  write: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  deploy: 'bg-green-100 text-green-700',
  manage_projects: 'bg-orange-100 text-orange-700',
  manage_users: 'bg-purple-100 text-purple-700',
  admin: 'bg-gray-800 text-white',
};

const GroupsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user: currentUser } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const canManage = currentUser?.role === 'owner' || currentUser?.role === 'admin';

  const showToast = useCallback((type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await groupsApi.listGroups();
      setGroups(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Impossible de charger les groupes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const handleDelete = async (group: Group) => {
    if (!window.confirm(`Supprimer le groupe "${group.name}" ?`)) return;
    try {
      await groupsApi.deleteGroup(group.id);
      showToast('success', 'Groupe supprime');
      loadGroups();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur lors de la suppression');
    }
    setActiveMenu(null);
  };

  return (
    <div className="p-8">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? <FiCheck size={16} /> : <FiAlertCircle size={16} />}
          {toast.msg}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('groups.header.title')}</h2>
          <p className="text-gray-600 mt-1">{t('groups.header.subtitle')}</p>
        </div>
        {canManage && (
          <button
            onClick={() => { setEditingGroup(null); setShowModal(true); }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <FiPlus size={18} />
            {t('groups.buttons.newGroup')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <FiAlertCircle size={32} className="mx-auto text-red-400 mb-2" />
          <p className="text-red-700">{error}</p>
          <button onClick={loadGroups} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Reessayer</button>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FiUsers size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun groupe</h3>
          <p className="text-gray-500 mb-6">Creez des groupes pour organiser les permissions par equipe.</p>
          {canManage && (
            <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
              <FiPlus size={20} /> Creer un groupe
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group) => (
            <div key={group.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FiUsers className="text-blue-600" size={22} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{group.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{group.member_count} membre{group.member_count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                {canManage && (
                  <div className="relative">
                    <button onClick={() => setActiveMenu(activeMenu === group.id ? null : group.id)} className="p-2 hover:bg-gray-100 rounded-full">
                      <FiMoreVertical className="text-gray-600" />
                    </button>
                    {activeMenu === group.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                        <button onClick={() => { setEditingGroup(group); setShowModal(true); setActiveMenu(null); }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                          <FiEdit2 size={16} /> Modifier
                        </button>
                        <button onClick={() => handleDelete(group)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600">
                          <FiTrash2 size={16} /> Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-600 mb-4 min-h-[2rem]">{group.description}</p>

              <div className="flex flex-wrap gap-1.5">
                {(Array.isArray(group.permissions) ? group.permissions : []).map((perm) => (
                  <span key={perm} className={`px-2 py-0.5 text-xs rounded-md font-medium ${PERM_COLOR[perm] || 'bg-gray-100 text-gray-700'}`}>
                    {ALL_PERMISSIONS.find(p => p.key === perm)?.label || perm}
                  </span>
                ))}
                {(!group.permissions || group.permissions.length === 0) && (
                  <span className="text-xs text-gray-400 italic">Aucune permission</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <GroupModal
          group={editingGroup}
          onClose={() => { setShowModal(false); setEditingGroup(null); }}
          onSave={async (data) => {
            try {
              if (editingGroup) {
                await groupsApi.updateGroup(editingGroup.id, data);
                showToast('success', 'Groupe mis a jour');
              } else {
                await groupsApi.createGroup(data as any);
                showToast('success', 'Groupe cree');
              }
              setShowModal(false);
              setEditingGroup(null);
              loadGroups();
            } catch (e: any) {
              showToast('error', e.response?.data?.error || 'Erreur');
            }
          }}
        />
      )}
    </div>
  );
};

interface GroupModalProps {
  group: Group | null;
  onClose: () => void;
  onSave: (data: { name: string; description: string; permissions: string[] }) => void;
}

const GroupModal: React.FC<GroupModalProps> = ({ group, onClose, onSave }) => {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [selectedPerms, setSelectedPerms] = useState<string[]>(
    Array.isArray(group?.permissions) ? group.permissions : []
  );

  const togglePermission = (perm: string) => {
    setSelectedPerms(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">{group ? 'Modifier le groupe' : 'Nouveau groupe'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ name, description, permissions: selectedPerms }); }} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du groupe</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {ALL_PERMISSIONS.map((perm) => (
                <label key={perm.key} className="flex items-start gap-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg">
                  <input type="checkbox" checked={selectedPerms.includes(perm.key)} onChange={() => togglePermission(perm.key)}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{perm.label}</span>
                    <p className="text-xs text-gray-400">{perm.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{group ? 'Enregistrer' : 'Creer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroupsPage;
